import { Component, OnInit, OnChanges, Input, Output, EventEmitter, signal, computed } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { FlowObjectDefinition, FlowObjectInstance, FlowObjectInstanceState, FlowObjectType, FlowObjectTypeCategory } from '../../../models/flow-object.model';
import { FlowDefinition, FlowInstance } from '../../../models/flow.model';
import { InputDataTaskCancelByDeliberation, OutputDataTaskCancelByDeliberation } from '../../../models/input-output-data.model';
import { Papel } from '../../../models/edocs.model';
import { AuthService } from '../../../services/auth.service';
import { EDocsService } from '../../../services/edocs.service';
import { Enums } from '../../../shared/enums';
import { Utils } from '../../../shared/utils';
import { IMPORTS } from './_imports';
import { NgxSpinnerService } from 'ngx-spinner';
import { PdfPreviewDialogComponent } from '../../pdf-preview-dialog/pdf-preview-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { ConfigSchema } from 'src/app/models/config-schema.model';

@Component({
    selector: 'fob-cancel-by-deliberation',
    templateUrl: './fob-cancel-by-deliberation.component.html',
    styleUrl: './fob-cancel-by-deliberation.component.scss',
    standalone: true,
    imports: IMPORTS
})
export class FlowObjectCancelByDeliberationComponent implements OnInit, OnChanges {
    // #region [Type props]
    FlowObjectInstanceState = FlowObjectInstanceState;
    FlowObjectType = FlowObjectType;
    Utils = Utils;
    // #endregion

    // #region [props]
    outputData: OutputDataTaskCancelByDeliberation;
    // #endregion

    // #region [signals]
    $model = signal(null as FlowObjectInstance);
    $flowInstance = signal(null as FlowInstance);
    $flowDefinition = signal(null as FlowDefinition);
    $inputData = signal(new InputDataTaskCancelByDeliberation());
    $flowObjectDefinition = signal(null as FlowObjectDefinition);
    $papeisSelector = signal([] as Papel[]);
    $previousDocuments = signal([] as any[]);
    // #endregion

    // #region [computeds]
    $currentUser = computed(() => this.authService.$user());
    $isReadOnly = computed(() => Utils.isNotNullOrEmpty(this.$flowInstance()?.finishDate));
    $ownerInfo = computed(() => this.$flowInstance()?.ownerInfo || '(...)');
    $isMissingMessage = computed(() => {
        const msg = this.$inputData()?.message;
        return Utils.isNullOrEmpty(msg) || (msg?.trim()?.length ?? 0) < 5;
    });
    $isTaskPublicAgentActor = computed(() =>
        this.$currentUser()?.isPublicAgent
        && (this.$model()?.flowObjectInstanceActors ?? []).some(x => x.actorId == this.$currentUser()?.id)
        && this.$flowInstance()?.ownerId != this.$currentUser()?.id
    );
    // #endregion

    // #region [Input/Output]
    @Input() _model: FlowObjectInstance;
    @Input() _flowInstance: FlowInstance;
    @Input() _flowDefinition: FlowDefinition;
    @Output() _submitEvent = new EventEmitter<FlowObjectInstance>();
    @Output() _discardEvent = new EventEmitter<void>();
    // #endregion

    constructor(
        private toastr: ToastrService,
        private authService: AuthService,
        private eDocsService: EDocsService,
        private spinner: NgxSpinnerService,
        private dialog: MatDialog,
    ) { }

    async ngOnInit() {
        Utils.setSignal(this.$flowObjectDefinition, this.$flowDefinition().flowObjectDefinitions.find(x => x.id == this.$model().flowObjectDefinitionId));
        Utils.setSignal(this.$inputData, Object.assign(new InputDataTaskCancelByDeliberation(), JSON.parse(this.$model().inputData)));

        const forwardingId = this.$inputData()?.eDocsData?.forwardingId;
        let encaminhamento;

        // #region [Encaminhamento]
        if (Utils.isNotNullOrEmpty(forwardingId)) {
            const response = await this.eDocsService.getUsuarioPapeisEncaminhamento(forwardingId);

            if (!response.isSuccess) {
                this.toastr.error(response.message.description, Enums.Messages.Error, Utils.getToastrErrorOptions());
                return;
            }

            this.$papeisSelector.set(response.data);

            if (this.$papeisSelector().length > 0) {
                setTimeout(() => Utils.updSignal(this.$inputData, x => {
                    x.eDocsData.signerId = null;
                    return x;
                }), 50);
            } else {
                Utils.updSignal(this.$inputData, x => {
                    x.eDocsData.signerId = this.$currentUser().id;
                    return x;
                });
            }

            const response_Encaminhamento = await this.eDocsService.getEncaminhamento(forwardingId);
            encaminhamento = response_Encaminhamento.data;
        }
        // #endregion

        // #region [documentos capturados anteriormente à Tarefa corrente]
        for (let item of encaminhamento?.documentosEncaminhamento.filter(x => !x.controle)) {
            // Se o documento pertence ao forwardingId da tarefa atual (outputData), paramos de adicionar aos "anteriores"
            if (Utils.isNotNullOrEmpty(this.outputData) && this.outputData.eDocsData.forwardingId == item.documento.encaminhamentoId) {
                break;
            }

            // Evita duplicados verificando o 'registro'
            if (!this.$previousDocuments().some(x => x.registro == item.documento.registro)) {
                Utils.updSignal(this.$previousDocuments, x => {
                    if (item.documento.nome.endsWith(FlowObjectTypeCategory.get(FlowObjectType.CancelByDeliberation))) {
                        x.push({
                            id: item.documento.id,
                            registro: item.documento.registro,
                            filename: `${item.documento.nome}.${item.documento.extensao}`
                        });
                    }
                    return x;
                });
            }
        }
        // #endregion

        // #region [Papel do executor]
        if (this.$flowInstance().finishDate != null) {
            Utils.updSignal(this.$inputData, x => {
                x.message = this.$flowInstance().cancellationMessage;
                x.eDocsData.signerId = this.$flowInstance().cancellationActorId;
                return x;
            });
        }
        // #endregion

        // #region [Tratamento especial para Tasks com opção de cancelamento obrigatória]
        const lastAddToProcessTask = this._flowInstance.flowObjectInstances.find(x => x.stateId == FlowObjectInstanceState.Started && x.flowObjectDefinition.typeId == FlowObjectType.TaskAddToProcess);
        if (lastAddToProcessTask != null) {
            const addToProcessConfigSchema = JSON.parse(lastAddToProcessTask.flowObjectDefinition.configSchema) as ConfigSchema;
            Utils.updSignal(this.$inputData, x => {
                x.cancellationDocumentClassId = addToProcessConfigSchema.taskAddToProcess.cancellationDocumentClass?.id;
                return x;
            });
        }

        const lastRegisterProcessProcessTask = this._flowInstance.flowObjectInstances.find(x => x.stateId == FlowObjectInstanceState.Started && x.flowObjectDefinition.typeId == FlowObjectType.TaskRegisterProcess);
        if (lastRegisterProcessProcessTask != null) {
            const registerProcessConfigSchema = JSON.parse(lastRegisterProcessProcessTask.flowObjectDefinition.configSchema) as ConfigSchema;
            Utils.updSignal(this.$inputData, x => {
                x.cancellationDocumentClassId = registerProcessConfigSchema.taskRegisterProcess.processClass?.id;
                return x;
            });
        }
        // #endregion
    }

    ngOnChanges() {
        this.$model.set(this._model);
        this.$flowInstance.set(this._flowInstance);
        this.$flowDefinition.set(this._flowDefinition);
    }

    signerIdChange(event: any) {
        Utils.updSignal(this.$inputData, x => {
            x.eDocsData.signerId = event;
            return x;
        });
    }

    onChangeMessage(event: string) {
        Utils.updSignal(this.$inputData, x => {
            x.message = event;
            return x;
        });
    }

    submit() {
        if (this.$isMissingMessage()) {
            this.toastr.warning(Enums.Messages.MessageMinlengthError, Enums.Messages.Warning, Utils.getToastrErrorOptions());
            return;
        }

        Utils.updSignal(this.$model, x => {
            x.inputData = JSON.stringify(this.$inputData());
            return x;
        });

        this._submitEvent.emit(this.$model());
    }

    async openDocumentModal(id: string) {
        this.spinner.show('flowInstance');

        const response = await this.eDocsService.getDocumentoDownloadUrl(id);

        this.spinner.hide('flowInstance');

        if (!response.isSuccess) {
            this.toastr.error(response.message.description, Enums.Messages.Error, Utils.getToastrErrorOptions());
            return;
        }

        setTimeout(() => {
            this.dialog.open(PdfPreviewDialogComponent, {
                data: {
                    content: response.data
                }
            });
        }, 200);
    }

    getDateString(): string {
        return new Date(this.$model().finishDate).toLocaleString();
    }

    discardManualCancel() {
        this._discardEvent.emit();
    }

    // #region [===== private methods =====]
    // #endregion
}
